import React, { useState } from "react";
import { Plus, X, MapPin, Truck, GraduationCap, Fuel, Shield, Building2, FileText, ChevronDown } from "lucide-react";

/* Definizione tipi di servizio con icone, label e campi specifici.
   Ogni servizio richiesto per un articolo del preventivo viene mostrato come "chip"
   rimovibile sotto l'articolo. */
export const SERVICE_TYPES = [
    {
        id: "delivery_site",
        label: "Consegna in cantiere",
        icon: MapPin,
        needsLocation: true,
        placeholder: "Indirizzo completo del cantiere (via, città, CAP)",
    },
    {
        id: "transport",
        label: "Trasporto incluso (a/r)",
        icon: Truck,
    },
    {
        id: "operator_training",
        label: "Formazione operatore",
        icon: GraduationCap,
    },
    {
        id: "fuel",
        label: "Carburante incluso",
        icon: Fuel,
    },
    {
        id: "insurance",
        label: "Assicurazione integrativa",
        icon: Shield,
    },
    {
        id: "pickup_nsp",
        label: "Ritiro presso sede NSP",
        icon: Building2,
        needsHeadquarters: true,
    },
    {
        id: "invoice_type",
        label: "Tipo fatturazione",
        icon: FileText,
        needsInvoiceMode: true,
    },
];

const HEADQUARTERS = [
    "BigMat S.p.A. - Via Salaria Vecchia 31, Osteria Nuova di Poggio Moiano (RI)",
];

const labelOfService = (svc) => SERVICE_TYPES.find((s) => s.id === svc.type)?.label || svc.type;
const iconOfService = (svc) => SERVICE_TYPES.find((s) => s.id === svc.type)?.icon || FileText;

export const ServicesManager = ({ services = [], onChange, itemTestId }) => {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [editing, setEditing] = useState(null); // service in fase di edit con campi extra

    const available = SERVICE_TYPES.filter(
        (t) => !services.find((s) => s.type === t.id)
    );

    const addService = (typeDef) => {
        setPickerOpen(false);
        if (typeDef.needsLocation || typeDef.needsHeadquarters || typeDef.needsInvoiceMode) {
            // Apre il form di dettaglio
            setEditing({
                type: typeDef.id,
                location: "",
                headquarters: typeDef.needsHeadquarters ? HEADQUARTERS[0] : undefined,
                invoice_mode: typeDef.needsInvoiceMode ? "company" : undefined,
                vat_number: "",
                notes: "",
            });
        } else {
            onChange([...services, { type: typeDef.id }]);
        }
    };

    const removeService = (type) => {
        onChange(services.filter((s) => s.type !== type));
    };

    const confirmEditing = () => {
        if (!editing) return;
        const def = SERVICE_TYPES.find((t) => t.id === editing.type);
        if (def?.needsLocation && !editing.location?.trim()) {
            return; // richiesto indirizzo
        }
        const cleaned = { type: editing.type };
        if (editing.location?.trim()) cleaned.location = editing.location.trim();
        if (editing.headquarters?.trim()) cleaned.headquarters = editing.headquarters;
        if (editing.invoice_mode) cleaned.invoice_mode = editing.invoice_mode;
        if (editing.vat_number?.trim()) cleaned.vat_number = editing.vat_number.trim();
        if (editing.notes?.trim()) cleaned.notes = editing.notes.trim();
        onChange([...services, cleaned]);
        setEditing(null);
    };

    return (
        <div className="mt-3" data-testid={`${itemTestId}-services`}>
            {/* Chip lista servizi già aggiunti */}
            {services.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                    {services.map((svc) => {
                        const Icon = iconOfService(svc);
                        return (
                            <span
                                key={svc.type}
                                data-testid={`${itemTestId}-service-${svc.type}`}
                                className="inline-flex items-center gap-1.5 bg-nsp-green text-nsp-yellow text-[11px] font-bold uppercase tracking-wide pl-2 pr-1 py-1 border-b-2 border-nsp-yellow"
                            >
                                <Icon className="w-3 h-3" />
                                <span>{labelOfService(svc)}</span>
                                {svc.location && (
                                    <span className="text-[10px] font-normal normal-case text-white/80 ml-1 max-w-[160px] truncate">
                                        · {svc.location}
                                    </span>
                                )}
                                {svc.headquarters && (
                                    <span className="text-[10px] font-normal normal-case text-white/80 ml-1 max-w-[160px] truncate">
                                        · {svc.headquarters}
                                    </span>
                                )}
                                {svc.invoice_mode && (
                                    <span className="text-[10px] font-normal normal-case text-white/80 ml-1">
                                        · {svc.invoice_mode === "company" ? "P.IVA" : "privato"}
                                    </span>
                                )}
                                <button
                                    onClick={() => removeService(svc.type)}
                                    className="ml-0.5 p-0.5 hover:bg-nsp-yellow hover:text-nsp-green rounded-sm transition-colors"
                                    aria-label={`Rimuovi ${labelOfService(svc)}`}
                                    data-testid={`${itemTestId}-service-remove-${svc.type}`}
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}

            {/* Pulsante aggiungi + menu opzioni */}
            {available.length > 0 && !editing && (
                <div className="relative">
                    <button
                        type="button"
                        data-testid={`${itemTestId}-add-service`}
                        onClick={() => setPickerOpen((o) => !o)}
                        className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-nsp-green border border-nsp-green/40 hover:bg-nsp-green hover:text-nsp-yellow px-3 py-1.5 transition-colors"
                    >
                        <Plus className="w-3.5 h-3.5" /> Aggiungi servizio
                        <ChevronDown className={`w-3 h-3 transition-transform ${pickerOpen ? "rotate-180" : ""}`} />
                    </button>

                    {pickerOpen && (
                        <div
                            data-testid={`${itemTestId}-services-picker`}
                            className="absolute z-30 mt-1 left-0 bg-white border border-nsp-border shadow-2xl min-w-[260px] max-w-[320px]"
                        >
                            {available.map((t) => {
                                const Icon = t.icon;
                                return (
                                    <button
                                        key={t.id}
                                        type="button"
                                        data-testid={`${itemTestId}-service-option-${t.id}`}
                                        onClick={() => addService(t)}
                                        className="w-full text-left flex items-center gap-2 px-3 py-2.5 hover:bg-nsp-bg text-xs border-b border-nsp-border/50 last:border-b-0"
                                    >
                                        <Icon className="w-4 h-4 text-nsp-green flex-shrink-0" />
                                        <span className="text-nsp-green font-semibold">
                                            {t.label}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Form dettagli (location / headquarters / invoice) */}
            {editing && (
                <div
                    data-testid={`${itemTestId}-service-form`}
                    className="bg-nsp-bg border border-nsp-green/30 p-3 space-y-2 mt-2"
                >
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-nsp-green">
                        Dettagli: {SERVICE_TYPES.find((t) => t.id === editing.type)?.label}
                    </div>

                    {SERVICE_TYPES.find((t) => t.id === editing.type)?.needsLocation && (
                        <input
                            type="text"
                            autoFocus
                            value={editing.location || ""}
                            onChange={(e) => setEditing({ ...editing, location: e.target.value })}
                            placeholder={SERVICE_TYPES.find((t) => t.id === editing.type)?.placeholder}
                            maxLength={200}
                            data-testid={`${itemTestId}-service-location-input`}
                            className="w-full h-10 bg-white border border-nsp-border px-2.5 text-xs focus:outline-none focus:border-nsp-green"
                        />
                    )}

                    {SERVICE_TYPES.find((t) => t.id === editing.type)?.needsHeadquarters && (
                        <select
                            value={editing.headquarters || HEADQUARTERS[0]}
                            onChange={(e) => setEditing({ ...editing, headquarters: e.target.value })}
                            data-testid={`${itemTestId}-service-hq-select`}
                            className="w-full h-10 bg-white border border-nsp-border px-2.5 text-xs focus:outline-none focus:border-nsp-green"
                        >
                            {HEADQUARTERS.map((hq) => (
                                <option key={hq} value={hq}>{hq}</option>
                            ))}
                        </select>
                    )}

                    {SERVICE_TYPES.find((t) => t.id === editing.type)?.needsInvoiceMode && (
                        <>
                            <div className="flex gap-2">
                                <label className="flex-1 flex items-center gap-2 p-2 border border-nsp-border bg-white cursor-pointer hover:border-nsp-green">
                                    <input
                                        type="radio"
                                        name="invoice_mode"
                                        checked={editing.invoice_mode === "company"}
                                        onChange={() => setEditing({ ...editing, invoice_mode: "company" })}
                                        data-testid={`${itemTestId}-service-invoice-company`}
                                        className="accent-nsp-green"
                                    />
                                    <span className="text-xs font-bold">Azienda (P.IVA)</span>
                                </label>
                                <label className="flex-1 flex items-center gap-2 p-2 border border-nsp-border bg-white cursor-pointer hover:border-nsp-green">
                                    <input
                                        type="radio"
                                        name="invoice_mode"
                                        checked={editing.invoice_mode === "private"}
                                        onChange={() => setEditing({ ...editing, invoice_mode: "private", vat_number: "" })}
                                        data-testid={`${itemTestId}-service-invoice-private`}
                                        className="accent-nsp-green"
                                    />
                                    <span className="text-xs font-bold">Privato</span>
                                </label>
                            </div>
                            {editing.invoice_mode === "company" && (
                                <input
                                    type="text"
                                    value={editing.vat_number || ""}
                                    onChange={(e) => setEditing({ ...editing, vat_number: e.target.value })}
                                    placeholder="P.IVA (opzionale, può essere comunicata dopo)"
                                    maxLength={30}
                                    data-testid={`${itemTestId}-service-vat-input`}
                                    className="w-full h-10 bg-white border border-nsp-border px-2.5 text-xs focus:outline-none focus:border-nsp-green"
                                />
                            )}
                        </>
                    )}

                    <div className="flex justify-end gap-2 pt-1">
                        <button
                            type="button"
                            onClick={() => setEditing(null)}
                            data-testid={`${itemTestId}-service-cancel`}
                            className="text-xs px-3 py-1.5 text-zinc-500 hover:text-nsp-green font-bold uppercase tracking-wider"
                        >
                            Annulla
                        </button>
                        <button
                            type="button"
                            onClick={confirmEditing}
                            data-testid={`${itemTestId}-service-confirm`}
                            className="text-xs px-3 py-1.5 bg-nsp-green text-nsp-yellow font-bold uppercase tracking-wider hover:bg-nsp-green-dark"
                        >
                            Conferma
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ServicesManager;
